import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarBlank, MapPin, PaperPlaneTilt, Confetti } from "@phosphor-icons/react";
import { api, eventPosterUrl, formatApiError } from "@/lib/api";
import { frDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";

function RegistrationForm({ eventId }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", participants: 1, message: "" });
    const [sending, setSending] = useState(false);
    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Nom et e-mail sont requis");
            return;
        }
        setSending(true);
        try {
            await api.post(`/events/${eventId}/register`, { ...form, participants: Number(form.participants) || 1 });
            toast.success("Inscription envoyée ! Le club vous recontactera.");
            setForm({ name: "", email: "", phone: "", participants: 1, message: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Une erreur est survenue");
        } finally {
            setSending(false);
        }
    };

    const field = "w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-clay focus:ring-2 focus:ring-clay/20 transition";

    return (
        <form onSubmit={submit} className="space-y-3" data-testid="event-registration-form">
            <div className="overline text-clay mb-1">S'inscrire</div>
            <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="reg-name" className={field} placeholder="Nom *" value={form.name} onChange={(e) => set("name", e.target.value)} />
                <input data-testid="reg-email" type="email" className={field} placeholder="E-mail *" value={form.email} onChange={(e) => set("email", e.target.value)} />
                <input data-testid="reg-phone" className={field} placeholder="Téléphone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                <input data-testid="reg-participants" type="number" min="1" className={field} placeholder="Nombre de participants" value={form.participants} onChange={(e) => set("participants", e.target.value)} />
            </div>
            <textarea data-testid="reg-message" rows={3} className={field} placeholder="Message (optionnel)" value={form.message} onChange={(e) => set("message", e.target.value)} />
            <button type="submit" disabled={sending} data-testid="reg-submit" className="inline-flex items-center gap-2 rounded-full bg-clay px-6 py-3 font-bold text-white transition-colors duration-200 hover:bg-clay-dark disabled:opacity-60">
                {sending ? "Envoi…" : <>Envoyer mon inscription <PaperPlaneTilt weight="bold" size={16} /></>}
            </button>
        </form>
    );
}

export default function Evenements() {
    const { data: events = [], isLoading } = useQuery({ queryKey: ["events"], queryFn: async () => (await api.get("/events")).data });

    return (
        <div data-testid="evenements-page">
            <PageHeader
                testid="evenements-header"
                overline="Vie du club"
                title="Événements"
                subtitle="Tournois, animations et rendez-vous du club. Inscrivez-vous directement en ligne."
            />

            <div className="max-w-6xl mx-auto px-4 md:px-6 py-12">
                {isLoading ? (
                    <p className="text-slate-500 font-mono text-sm">Chargement…</p>
                ) : events.length === 0 ? (
                    <div className="brand-card p-12 text-center" data-testid="evenements-empty">
                        <Confetti weight="duotone" size={44} className="mx-auto text-slate-300 mb-4" />
                        <p className="font-display font-bold text-navy text-lg">Aucun événement pour le moment</p>
                        <p className="text-slate-500 text-sm mt-1">Les prochains événements du club apparaîtront ici.</p>
                    </div>
                ) : (
                    <div className="space-y-10">
                        {events.map((ev, i) => (
                            <motion.article
                                key={ev.id}
                                data-testid="event-card"
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.05 }}
                                className="brand-card overflow-hidden grid lg:grid-cols-2"
                            >
                                <div className="bg-navy-deep min-h-[220px]">
                                    {ev.storage_path ? (
                                        <img src={eventPosterUrl(ev.id)} alt={`Affiche ${ev.title}`} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="h-full min-h-[240px] grid place-items-center court-lines">
                                            <Confetti weight="duotone" size={56} className="text-white/40" />
                                        </div>
                                    )}
                                </div>
                                <div className="p-6 md:p-8">
                                    <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy tracking-tight">{ev.title}</h2>
                                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
                                        {ev.date && <span className="inline-flex items-center gap-1.5"><CalendarBlank weight="fill" size={16} className="text-clay" /> {frDate(ev.date, { weekday: "long" })}</span>}
                                        {ev.location && <span className="inline-flex items-center gap-1.5"><MapPin weight="fill" size={16} className="text-clay" /> {ev.location}</span>}
                                    </div>
                                    {ev.description && <p className="mt-4 text-slate-600 leading-relaxed whitespace-pre-line">{ev.description}</p>}
                                    <div className="mt-6 pt-6 border-t border-slate-100">
                                        <RegistrationForm eventId={ev.id} />
                                    </div>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
